import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { stationAssetsApi } from '../api/stationAssetsApi';
import type {
  AreaFormValues,
  StationComplianceProfileFormValues,
  TankFormValues
} from '../types/stationAssets';

const resolveAssetStationId = (asset: unknown) => {
  if (!asset || typeof asset !== 'object') {
    return undefined;
  }

  const record = asset as Record<string, unknown>;
  const station = record.station ?? record.station_id ?? record.stationId;

  if (station && typeof station === 'object' && 'id' in station) {
    return String((station as { id: string | number }).id);
  }

  if (typeof station === 'string' || typeof station === 'number' || typeof station === 'boolean') {
    return String(station);
  }

  return undefined;
};

const matchesStation = (stationId: string | number, asset: unknown) =>
  String(stationId) === resolveAssetStationId(asset);

export const getUserOrganizationId = (user: ReturnType<typeof useAuth>['user']) => {
  const organization = user?.organization;

  if (organization && typeof organization === 'object') {
    return String(organization.id);
  }

  if (organization !== null && organization !== undefined) {
    return String(organization);
  }

  return undefined;
};

export const stationAssetKeys = {
  stations: (organizationId?: string) => ['stations', organizationId ?? 'none'] as const,
  products: (organizationId?: string) => ['products', organizationId ?? 'none'] as const,
  areas: (organizationId?: string, stationId?: string) =>
    ['areas', organizationId ?? 'none', stationId ?? 'none'] as const,
  area: (organizationId?: string, areaId?: string) =>
    ['area', organizationId ?? 'none', areaId ?? 'none'] as const,
  tanks: (organizationId?: string, stationId?: string) =>
    ['tanks', organizationId ?? 'none', stationId ?? 'none'] as const,
  tank: (organizationId?: string, tankId?: string) =>
    ['tank', organizationId ?? 'none', tankId ?? 'none'] as const,
  tankAllowedProducts: (organizationId?: string, stationId?: string, tankId?: string) =>
    [
      'tank-allowed-products',
      organizationId ?? 'none',
      stationId ?? 'none',
      tankId ?? 'none'
    ] as const,
  dispensers: (organizationId?: string, stationId?: string) =>
    ['dispensers', organizationId ?? 'none', stationId ?? 'none'] as const,
  complianceProfile: (organizationId?: string, stationId?: string) =>
    ['station-compliance-profile', organizationId ?? 'none', stationId ?? 'none'] as const
};

export const useStations = () => {
  const { user } = useAuth();
  const organizationId = getUserOrganizationId(user);

  return useQuery({
    queryKey: stationAssetKeys.stations(organizationId),
    queryFn: () => stationAssetsApi.listStations(),
    enabled: Boolean(user),
    networkMode: 'offlineFirst'
  });
};

export const useAreasByStation = (stationId?: string) => {
  const { user } = useAuth();
  const organizationId = getUserOrganizationId(user);
  const query = useQuery({
    queryKey: stationAssetKeys.areas(organizationId, stationId),
    queryFn: () => stationAssetsApi.listAreas(stationId),
    enabled: Boolean(user && stationId),
    networkMode: 'offlineFirst'
  });

  return {
    ...query,
    data: (query.data ?? []).filter((area) => stationId && matchesStation(stationId, area))
  };
};

export const useArea = (areaId?: string) => {
  const { user } = useAuth();
  const organizationId = getUserOrganizationId(user);

  return useQuery({
    queryKey: stationAssetKeys.area(organizationId, areaId),
    queryFn: () => stationAssetsApi.getArea(areaId ?? ''),
    enabled: Boolean(user && areaId),
    networkMode: 'offlineFirst'
  });
};

export const useProducts = () => {
  const { user } = useAuth();
  const organizationId = getUserOrganizationId(user);

  return useQuery({
    queryKey: stationAssetKeys.products(organizationId),
    queryFn: () => stationAssetsApi.listProducts(),
    enabled: Boolean(user),
    networkMode: 'offlineFirst'
  });
};

export const useTanksByStation = (stationId?: string) => {
  const { user } = useAuth();
  const organizationId = getUserOrganizationId(user);
  const query = useQuery({
    queryKey: stationAssetKeys.tanks(organizationId, stationId),
    queryFn: () => stationAssetsApi.listTanks(stationId),
    enabled: Boolean(user && stationId),
    networkMode: 'offlineFirst'
  });

  return {
    ...query,
    data: (query.data ?? []).filter((tank) => stationId && matchesStation(stationId, tank))
  };
};

export const useTank = (tankId?: string) => {
  const { user } = useAuth();
  const organizationId = getUserOrganizationId(user);

  return useQuery({
    queryKey: stationAssetKeys.tank(organizationId, tankId),
    queryFn: () => stationAssetsApi.getTank(tankId ?? ''),
    enabled: Boolean(user && tankId),
    networkMode: 'offlineFirst'
  });
};

export const useTankAllowedProducts = (stationId?: string, tankId?: string) => {
  const { user } = useAuth();
  const organizationId = getUserOrganizationId(user);

  return useQuery({
    queryKey: stationAssetKeys.tankAllowedProducts(organizationId, stationId, tankId),
    queryFn: () => stationAssetsApi.getTankAllowedProducts(stationId ?? '', tankId ?? ''),
    enabled: Boolean(user && stationId && tankId),
    networkMode: 'offlineFirst',
    staleTime: 5 * 60 * 1000
  });
};

export const useDispensersByStation = (stationId?: string) => {
  const { user } = useAuth();
  const organizationId = getUserOrganizationId(user);
  const query = useQuery({
    queryKey: stationAssetKeys.dispensers(organizationId, stationId),
    queryFn: () => stationAssetsApi.listDispensers(stationId),
    enabled: Boolean(user && stationId),
    networkMode: 'offlineFirst'
  });

  return {
    ...query,
    data: (query.data ?? []).filter(
      (dispenser) => stationId && matchesStation(stationId, dispenser)
    )
  };
};

export const useStationComplianceProfile = (stationId?: string) => {
  const { user } = useAuth();
  const organizationId = getUserOrganizationId(user);

  return useQuery({
    queryKey: stationAssetKeys.complianceProfile(organizationId, stationId),
    queryFn: () => stationAssetsApi.getStationComplianceProfile(stationId ?? ''),
    enabled: Boolean(user && stationId),
    networkMode: 'offlineFirst'
  });
};

export const useUpdateStationComplianceProfile = (stationId: string) => {
  const { user } = useAuth();
  const organizationId = getUserOrganizationId(user);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: StationComplianceProfileFormValues) =>
      stationAssetsApi.updateStationComplianceProfile(stationId, values),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: stationAssetKeys.complianceProfile(organizationId, stationId)
      });
    }
  });
};

export const useCreateArea = (stationId: string) => {
  const { user } = useAuth();
  const organizationId = getUserOrganizationId(user);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: AreaFormValues) => stationAssetsApi.createArea(values),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: stationAssetKeys.areas(organizationId, stationId)
      });
    }
  });
};

export const useUpdateArea = (stationId: string, areaId: string) => {
  const { user } = useAuth();
  const organizationId = getUserOrganizationId(user);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: Partial<AreaFormValues>) => stationAssetsApi.updateArea(areaId, values),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: stationAssetKeys.areas(organizationId, stationId)
        }),
        queryClient.invalidateQueries({
          queryKey: stationAssetKeys.area(organizationId, areaId)
        }),
        queryClient.invalidateQueries({
          queryKey: stationAssetKeys.tanks(organizationId, stationId)
        }),
        queryClient.invalidateQueries({
          queryKey: stationAssetKeys.dispensers(organizationId, stationId)
        })
      ]);
    }
  });
};

export const useCreateTank = (stationId: string) => {
  const { user } = useAuth();
  const organizationId = getUserOrganizationId(user);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: TankFormValues) => stationAssetsApi.createTank(values),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: stationAssetKeys.tanks(organizationId, stationId)
      });
    }
  });
};

export const useUpdateTank = (stationId: string, tankId: string) => {
  const { user } = useAuth();
  const organizationId = getUserOrganizationId(user);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: Partial<TankFormValues>) => stationAssetsApi.updateTank(tankId, values),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: stationAssetKeys.tanks(organizationId, stationId)
        }),
        queryClient.invalidateQueries({
          queryKey: stationAssetKeys.tank(organizationId, tankId)
        }),
        queryClient.invalidateQueries({
          queryKey: stationAssetKeys.tankAllowedProducts(organizationId, stationId, tankId)
        })
      ]);
    }
  });
};
